import { HeadsetVersion } from "types/productTypes";
import { TestCategory, TestLanguage, TestType } from "types/testTypes";
import { OculeraFeatureKey } from "types/versionTypes";

export const CATEGORY_VERSION_MAP: Record<TestCategory, HeadsetVersion | null> = {
  [TestCategory.VisualField]: null,
  [TestCategory.FDT]: HeadsetVersion.ver_2_7,
  [TestCategory.ContrastSensitivity]: HeadsetVersion.ver_2_7,
  [TestCategory.VisualAcuity]: HeadsetVersion.ver_2_7,
  [TestCategory.ColorVision]: HeadsetVersion.ver_2_7,
  [TestCategory.AmslerGrid]: HeadsetVersion.ver_2_7,
  [TestCategory.Pupillometry]: HeadsetVersion.ver_2_7,
  [TestCategory.Dyslexia]: HeadsetVersion.ver_2_7,
  [TestCategory.ReadingAssessment]: HeadsetVersion.ver_2_7_14,
  [TestCategory.ContinuousPerformance]: HeadsetVersion.ver_2_7,
  [TestCategory.OcularMotility]: HeadsetVersion.ver_2_7,
  [TestCategory.Cover]: HeadsetVersion.ver_2_7_1,
  [TestCategory.StereoAcuity]: HeadsetVersion.ver_2_8_4,
  [TestCategory.Kinetic]: HeadsetVersion.ver_2_7_12,
  [TestCategory.Videonystagmography]: HeadsetVersion.ver_2_7_8,
  [TestCategory.Dyscalculia]: HeadsetVersion.ver_2_7_14,
  [TestCategory.WisconsinCardSorting]: HeadsetVersion.ver_2_8,
  [TestCategory.BinocularVision]: HeadsetVersion.ver_2_8_5,
  [TestCategory.OptokineticNystagmus]: HeadsetVersion.ver_2_8_5,
};

export const FEATURE_VERSION_MAP: Record<OculeraFeatureKey, HeadsetVersion> = {
  sceneUpdateMessages: HeadsetVersion.ver_2_3,
  newLanguages2: HeadsetVersion.ver_2_6_5,
  newLanguages3: HeadsetVersion.ver_2_6_6,
  newPatterns: HeadsetVersion.ver_2_6_2,
  usbUpdateNeeded: HeadsetVersion.ver_2_6_7,
  swap: HeadsetVersion.ver_2_7_12,
  minVirtualAssistant: HeadsetVersion.ver_2_7_9,
  fixationHandling: HeadsetVersion.ver_2_7_9,
  fdtGazeAutoPause: HeadsetVersion.ver_2_7_9,
  colorVisionSingleEyeTestSupport: HeadsetVersion.ver_2_7_11,
  amslerGridSingleEyeTestSupport: HeadsetVersion.ver_2_7_11,
  deviceVolumeControl: HeadsetVersion.ver_2_7_12,
  vfaFovealThresholdTesting: HeadsetVersion.ver_2_6_5,
  tumblingESceneReadyMessage: HeadsetVersion.ver_2_7_14,
  skipVirtualAssistance: HeadsetVersion.ver_2_7_14,
  startWithCalibrationScene: HeadsetVersion.ver_2_8_3,
  sceneReadyMessage: HeadsetVersion.ver_2_7_14,
  tumblingEUnityControlledRegularMode: HeadsetVersion.ver_2_8_3,
  colorVisionAutomaticHeadsetDriven: HeadsetVersion.ver_2_8_5,
  extraTestCalibrationToggle: HeadsetVersion.ver_2_8_5,
  testSession: HeadsetVersion.ver_2_8_5,
  operatorPauseTest: HeadsetVersion.ver_2_8_5,
  deviceIdleTimeouts: HeadsetVersion.ver_2_8_5,
  vfaZoneTests: HeadsetVersion.ver_2_8_6,
  vfaMidTestGazeAutoPauseToggle: HeadsetVersion.ver_2_8_5,
  eyeTrackingCalibrationVaInstructions: HeadsetVersion.ver_2_8_6,
  assistantAudio: HeadsetVersion.ver_2_8_6,
  headsetSceneView: HeadsetVersion.ver_2_8_6,
  playerPrefLiveApply: HeadsetVersion.ver_2_8_5,
  disableKeyConfigOnTest: HeadsetVersion.ver_2_8_6,
  logSinkPrefs: HeadsetVersion.ver_2_8_6,
  dayLogDownload: HeadsetVersion.ver_2_8_6,
  smoothPursuitChildMode: HeadsetVersion.ver_2_8_6,
  tutorialMode: HeadsetVersion.ver_2_8_6,
  vfaAdaptiveTracking: HeadsetVersion.ver_2_8_6,
  readingAssessmentPageChangeInteraction: HeadsetVersion.ver_2_8_6,
  deviceReadinessCheck: HeadsetVersion.ver_2_8_6,
  vfaMovingFixationCatchTrials: HeadsetVersion.ver_2_9,
};

export const TEST_TYPE_VERSION_MAP: Record<TestType, HeadsetVersion> = {
  [TestType.Threshold]: HeadsetVersion.ver_2_7,
  [TestType.Ascending]: HeadsetVersion.ver_2_7,
  [TestType.PelliRobson]: HeadsetVersion.ver_2_7,
  [TestType.ReadingAssessment]: HeadsetVersion.ver_2_7_14,
  [TestType.ContinuousPerformance]: HeadsetVersion.ver_2_7,
  [TestType.GoNoGo]: HeadsetVersion.ver_2_7,
  [TestType.IdenticalPairs]: HeadsetVersion.ver_2_7,
  [TestType.Logmar]: HeadsetVersion.ver_2_7,
  [TestType.Chart]: HeadsetVersion.ver_2_7,
  [TestType.Pupillometry]: HeadsetVersion.ver_2_7,
  [TestType.RAPD]: HeadsetVersion.ver_2_7,
  [TestType.CircularPursuit]: HeadsetVersion.ver_2_7,
  [TestType.XIH]: HeadsetVersion.ver_2_7,
  [TestType.C_20_1]: HeadsetVersion.ver_2_7,
  [TestType.C_20_5]: HeadsetVersion.ver_2_7,
  [TestType.N_30_1]: HeadsetVersion.ver_2_7,
  [TestType.N_30_5]: HeadsetVersion.ver_2_7,
  [TestType.Staircase]: HeadsetVersion.ver_2_7,
  [TestType.Videonystagmography]: HeadsetVersion.ver_2_7,
  [TestType.PER_24_2]: null,
  [TestType.PER_24_2C]: HeadsetVersion.ver_2_9,
  [TestType.PER_30_2]: null,
  [TestType.PER_10_2]: null,
  [TestType.PER_BINOCULAR]: FEATURE_VERSION_MAP.newPatterns,
  [TestType.PER_BINOCULAR2]: FEATURE_VERSION_MAP.newPatterns,
  [TestType.PER_BINOCULAR3]: HeadsetVersion.ver_2_8_5,
  [TestType.PER_C_40]: FEATURE_VERSION_MAP.newPatterns,
  [TestType.PER_C_76]: FEATURE_VERSION_MAP.newPatterns,
  [TestType.PER_SUPERIOR36]: FEATURE_VERSION_MAP.newPatterns,
  [TestType.PER_SUPERIOR64]: HeadsetVersion.ver_2_8_1,
  [TestType.PER_FF_120]: FEATURE_VERSION_MAP.newPatterns,
  [TestType.VisualField]: null,
  [TestType.ContrastSensitivity]: HeadsetVersion.ver_2_7,
  [TestType.ColorVision]: HeadsetVersion.ver_2_7,
  [TestType.VisualAcuity]: HeadsetVersion.ver_2_7,
  [TestType.CoverUncover]: HeadsetVersion.ver_2_7_1,
  [TestType.ObjectivePhoria]: HeadsetVersion.ver_2_7_1,
  [TestType.TumblingE]: HeadsetVersion.ver_2_7_10,
  [TestType.TumblingEThreshold]: HeadsetVersion.ver_2_8_1,
  [TestType.FDT]: HeadsetVersion.ver_2_7,
  [TestType.AutoKinetic]: HeadsetVersion.ver_2_7_12,
  [TestType.NumericalCognition]: HeadsetVersion.ver_2_7_14,
  [TestType.ColorVisionAutomatic]: HeadsetVersion.ver_2_8,
  [TestType.WCST]: HeadsetVersion.ver_2_8,
  [TestType.NearPointConvergence]: HeadsetVersion.ver_2_8_5,
  [TestType.SmoothPursuit]: HeadsetVersion.ver_2_8_5,
  [TestType.AntiSaccades]: HeadsetVersion.ver_2_8_5,
  [TestType.OptokineticNystagmus]: HeadsetVersion.ver_2_8_5,
};

export const VIRTUAL_ASSISTANT_LANGUAGE_VERSION_MAP: Record<TestLanguage, HeadsetVersion> = {
  [TestLanguage.NOT_SPECIFIED]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.English]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Turkish]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Arabic]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.French]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Czech]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.German]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Greek]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Spanish]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Persian]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Italian]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Japanese]: HeadsetVersion.n_a,
  [TestLanguage.Korean]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Malay]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Romanian]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Swedish]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Urdu]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Vietnamese]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Chinese]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Bulgarian]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Danish]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Hungarian]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.NorwegianBokmal]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Dutch]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Polish]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Portuguese]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Russian]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Thai]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Latvian]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Finnish]: HeadsetVersion.ver_2_7_9,
  [TestLanguage.Indonesian]: HeadsetVersion.ver_2_7_9,
};

export const DYSLEXIA_LANGUAGE_VERSION_MAP: Record<TestLanguage, HeadsetVersion> = {
  [TestLanguage.NOT_SPECIFIED]: HeadsetVersion.ver_2_7,
  [TestLanguage.English]: HeadsetVersion.ver_2_7,
  [TestLanguage.Turkish]: HeadsetVersion.ver_2_7,
  [TestLanguage.Arabic]: HeadsetVersion.ver_2_7,
  [TestLanguage.French]: HeadsetVersion.n_a,
  [TestLanguage.Czech]: HeadsetVersion.n_a,
  [TestLanguage.German]: HeadsetVersion.n_a,
  [TestLanguage.Greek]: HeadsetVersion.n_a,
  [TestLanguage.Spanish]: HeadsetVersion.n_a,
  [TestLanguage.Persian]: HeadsetVersion.n_a,
  [TestLanguage.Italian]: HeadsetVersion.n_a,
  [TestLanguage.Japanese]: HeadsetVersion.n_a,
  [TestLanguage.Korean]: HeadsetVersion.n_a,
  [TestLanguage.Malay]: HeadsetVersion.n_a,
  [TestLanguage.Romanian]: HeadsetVersion.n_a,
  [TestLanguage.Swedish]: HeadsetVersion.n_a,
  [TestLanguage.Urdu]: HeadsetVersion.n_a,
  [TestLanguage.Vietnamese]: HeadsetVersion.n_a,
  [TestLanguage.Chinese]: HeadsetVersion.n_a,
  [TestLanguage.Bulgarian]: HeadsetVersion.n_a,
  [TestLanguage.Danish]: HeadsetVersion.n_a,
  [TestLanguage.Hungarian]: HeadsetVersion.n_a,
  [TestLanguage.NorwegianBokmal]: HeadsetVersion.n_a,
  [TestLanguage.Dutch]: HeadsetVersion.n_a,
  [TestLanguage.Polish]: HeadsetVersion.n_a,
  [TestLanguage.Portuguese]: HeadsetVersion.n_a,
  [TestLanguage.Russian]: HeadsetVersion.n_a,
  [TestLanguage.Thai]: HeadsetVersion.n_a,
  [TestLanguage.Latvian]: HeadsetVersion.n_a,
  [TestLanguage.Finnish]: HeadsetVersion.n_a,
  [TestLanguage.Indonesian]: HeadsetVersion.n_a,
};
